/**
 * OnboardingHero — the zero-state panel for a fresh install.
 *
 * Rendered on the Dashboard when there are NO agents AND no legacy
 * migration is pending. It replaces the old EmptyState so the very
 * first screen a user sees feels like a welcome mat, not a "nothing
 * found" box.
 *
 * Three small hint cards below the CTA set the expectation for the
 * three-step setup the wizard is about to walk the user through:
 *   1. Bring your Discord bot token
 *   2. Pick channels the bot can listen to
 *   3. Click Deploy and chat
 *
 * Keep the visuals restrained — this is still the Linear-density
 * aesthetic, not a marketing splash page.
 */
import { Link } from 'react-router-dom';
import { Bot, KeyRound, MessagesSquare, Plus, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function OnboardingHero() {
  return (
    <div className="text-center py-20 px-8" aria-labelledby="onboarding-hero-title">
      <div className="inline-flex items-center justify-center h-20 w-20 rounded-2xl bg-primary/10 mb-6">
        <Bot className="h-10 w-10 text-primary" aria-hidden />
      </div>
      <h1
        id="onboarding-hero-title"
        className="text-3xl font-semibold tracking-tight"
      >
        Welcome to AgentHub
      </h1>
      <p className="mt-3 text-muted-foreground max-w-md mx-auto">
        You don't have any agents yet. Create your first one to start chatting
        with it in Discord.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3">
        <Button asChild size="lg">
          <Link to="/agents/new">
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Create your first agent
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <a
            href="/opt/agent-hub/panel/README.md"
            target="_blank"
            rel="noreferrer"
          >
            Docs
          </a>
        </Button>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3 max-w-2xl mx-auto text-left">
        <HintCard
          icon={KeyRound}
          title="Bring your Discord token"
          description="Create a bot application on Discord and paste the token. We validate it before anything else touches disk."
        />
        <HintCard
          icon={MessagesSquare}
          title="Configure channels"
          description="Pick the channels your bot should listen to — no more copy-pasting snowflake IDs from the Developer Mode menu."
        />
        <HintCard
          icon={Zap}
          title="Deploy in seconds"
          description="Mission Control writes the agent files, starts the systemd unit, and confirms the bot is online — end-to-end."
        />
      </div>
    </div>
  );
}

function HintCard({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Bot;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="mt-3 text-sm font-semibold tracking-tight">{title}</div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {description}
      </p>
    </div>
  );
}
